import { useState, type ReactNode } from "react";
import { Button, Dialog, TextField, vars } from "@pack-it-up/ui-web";
import { MAX_TAG_LENGTH, normalizeTag, tagKey } from "@pack-it-up/core";

// Issue #181 — rename a tag across the whole list. Renaming onto a tag that already
// exists *merges* the two, which can't be reconstructed from the result, so the dialog
// says so before the write and the caller offers an Undo after it.
export const RenameTagDialog = ({
  tag,
  palette,
  onClose,
  onRename,
}: {
  /** The tag being renamed; `null` closes the dialog. */
  tag: string | null;
  /** Every tag in use on this list, to detect a merge. */
  palette: string[];
  onClose: () => void;
  onRename: (from: string, to: string) => void;
}): ReactNode => {
  const [draft, setDraft] = useState(tag ?? "");
  // Remount per tag so the field starts from the tag being renamed rather than the
  // previous one — cheaper than an effect syncing state to a prop.
  const normalized = normalizeTag(draft);
  const unchanged = normalized === null || normalized === tag;
  const merges =
    normalized !== null &&
    tagKey(normalized) !== tagKey(tag ?? "") &&
    palette.some((t) => tagKey(t) === tagKey(normalized));

  return (
    <Dialog
      open={tag !== null}
      onClose={onClose}
      title="Rename tag"
      compact
      footer={
        <span style={{ display: "flex", justifyContent: "flex-end", gap: vars.space.sm }}>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={unchanged}
            onClick={() => {
              if (tag !== null && normalized !== null) onRename(tag, normalized);
              onClose();
            }}
          >
            {merges ? "Merge" : "Rename"}
          </Button>
        </span>
      }
    >
      <TextField
        label={`Rename "${tag ?? ""}" everywhere on this list`}
        value={draft}
        onChange={setDraft}
        maxLength={MAX_TAG_LENGTH}
        autoFocus
      />
      {merges && (
        <p style={{ fontSize: vars.font.sizeSm, color: vars.color.textMuted, margin: `${vars.space.sm} 0 0` }}>
          &ldquo;{normalized}&rdquo; already exists — the two tags will be merged into one. Undo
          restores them.
        </p>
      )}
    </Dialog>
  );
};
